import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Users, Loader2, Merge, AlertTriangle, RefreshCw, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ATTACHED_LABELS = {
  registrations: "registration",
  session_rsvps: "availability reply",
  academy_attendance: "attendance mark",
  attendance: "attendance mark",
  academy_credits: "credit",
  session_credits: "session credit",
  academy_feedback: "feedback note",
  kit_entitlements: "kit entitlement",
  payment_transactions: "payment",
  billing_events: "billing event",
  academy_enrollments: "enrolment",
  adhoc_bookings: "ad-hoc booking",
  academy_team_moves: "team move",
};

const describe = (attached) => {
  const parts = Object.entries(attached || {}).map(([k, n]) => {
    const label = ATTACHED_LABELS[k] || k.replace(/_/g, " ");
    return `${n} ${label}${n === 1 ? "" : "s"}`;
  });
  return parts.length ? parts.join(" · ") : "nothing attached";
};

export default function AdminDuplicatePlayers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keepers, setKeepers] = useState({});
  const [merging, setMerging] = useState("");
  const [splitting, setSplitting] = useState("");
  const [editing, setEditing] = useState("");
  const [edits, setEdits] = useState({});
  const [savingEdit, setSavingEdit] = useState("");

  const saveRecord = async (rec) => {
    const patch = edits[rec.player_id] || {};
    if (!patch.name && !patch.birthdate) {
      toast.info("Change the name or birthdate first");
      return;
    }
    setSavingEdit(rec.player_id);
    try {
      await axios.post(`${API}/academy/admin/duplicate-players/fix-record`,
        { player_id: rec.player_id, ...patch }, { withCredentials: true });
      toast.success("Record corrected");
      setEditing("");
      setEdits({ ...edits, [rec.player_id]: {} });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that");
    } finally {
      setSavingEdit("");
    }
  };

  const splitClash = async (clash) => {
    if (!window.confirm(
      `Give each copy of ${clash.name || "this record"} its own id?\n\n`
      + "The oldest keeps the id everything already points at. Once split you can merge them "
      + "normally."
    )) return;
    setSplitting(clash.player_id);
    try {
      const r = await axios.post(`${API}/academy/admin/duplicate-players/split-id-clash`,
        { player_id: clash.player_id }, { withCredentials: true });
      toast.success(`Split · ${r.data.new_ids.length} record(s) given a new id`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not split those");
    } finally {
      setSplitting("");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academy/admin/duplicate-players`, { withCredentials: true });
      setData(r.data);
      const picks = {};
      (r.data.groups || []).forEach((g, i) => { picks[i] = g.suggested_keep_id; });
      setKeepers(picks);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the duplicates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const merge = async (group, index) => {
    const keepId = keepers[index];
    const mergeIds = group.records.map((r) => r.player_id).filter((id) => id !== keepId);
    const keeper = group.records.find((r) => r.player_id === keepId);
    if (!window.confirm(
      `Merge ${mergeIds.length} record(s) into ${keeper.membership_number || keepId} for ${group.name}?\n\n`
      + "Everything attached to the others — registrations, attendance, credits, kit, availability — "
      + "moves across. The spare records are retired, not deleted, and point at the one you keep.\n\n"
      + `${group.name} keeps the number ${keeper.membership_number || "(none)"}.`
    )) return;

    setMerging(`${index}`);
    try {
      const r = await axios.post(`${API}/academy/admin/duplicate-players/merge`,
        { keep_id: keepId, merge_ids: mergeIds }, { withCredentials: true });
      const movedCount = Object.values(r.data.records_moved || {}).reduce((a, b) => a + b, 0);
      toast.success(`${group.name} merged · ${movedCount} record(s) moved across`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not merge those");
    } finally {
      setMerging("");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"
          data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Admin Panel
        </Link>

        <header>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-amber-400" /> Duplicate players
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            One child should have <strong className="text-slate-200">one record and one membership
            number</strong> for life. Where a child ended up with more than one, merge them onto the
            record the family already knows — usually the oldest. Everything attached to the spares
            moves across, and nothing is deleted.
          </p>
        </header>

        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            className="border-slate-700" data-testid="refresh-duplicates">
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <RefreshCw className="w-3 h-3 mr-1" />}
            Refresh
          </Button>
          {data && (
            <span className="text-sm text-slate-400" data-testid="duplicate-summary">
              {data.total_groups} child(ren) affected · {data.total_duplicates} spare record(s)
            </span>
          )}
        </div>

        {(data?.id_clashes || []).length > 0 && (
          <Card className="bg-red-500/5 border-red-500/40" data-testid="id-clash-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-red-200">
                <AlertTriangle className="w-5 h-5" /> Records sharing one id
              </CardTitle>
              <p className="text-xs text-slate-400">
                Two records are using the same internal id, so the portal returns whichever it
                finds first. Split them before merging anything else for these children.
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.id_clashes.map((clash) => (
                <div key={clash.player_id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2"
                  data-testid={`id-clash-${clash.player_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-white">{clash.name || "(no name)"}</p>
                    <p className="text-[11px] text-slate-500">
                      {clash.copies} copies · numbers {clash.membership_numbers.filter(Boolean).join(", ") || "none"}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" disabled={splitting === clash.player_id}
                    className="h-9 text-[11px] border-red-500/40 text-red-200"
                    onClick={() => splitClash(clash)}
                    data-testid={`split-clash-${clash.player_id}`}>
                    {splitting === clash.player_id
                      ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                    Give each its own id
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {loading && <p className="text-sm text-slate-400">Looking through every player record…</p>}

        {!loading && data && data.total_groups === 0 && (
          <Card className="bg-emerald-500/5 border-emerald-500/30">
            <CardContent className="py-6">
              <p className="text-sm text-emerald-200" data-testid="no-duplicates">
                No duplicates. Every child has exactly one live record.
              </p>
            </CardContent>
          </Card>
        )}

        {(data?.retired || []).length > 0 && (
          <Card className="bg-slate-900/60 border-slate-800" data-testid="retired-records-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Retired records ({data.retired.length})
              </CardTitle>
              <p className="text-xs text-slate-400">
                Already merged away or archived, so they are left out of the scan above.
                They stay hidden from parents — listed here so you can see where a credit sits.
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.retired.map((r) => (
                <div
                  key={r.player_id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded border
                             border-slate-800 bg-slate-800/40 px-3 py-2 text-sm"
                  data-testid={`retired-record-${r.player_id}`}
                >
                  <div>
                    <span className="text-white">{r.name}</span>
                    <span className="ml-2 text-xs text-slate-400">
                      {r.status}{r.merged_into ? " → kept record" : ""}
                      {r.parent_name ? ` · ${r.parent_name}` : ""}
                    </span>
                  </div>
                  {r.credit_on_this_record > 0 && (
                    <span
                      className="rounded bg-amber-500/15 px-2 py-0.5 text-xs text-amber-300"
                      data-testid={`retired-credit-${r.player_id}`}
                    >
                      ${r.credit_on_this_record.toFixed(2)} credit sits here — it still applies
                      to the family&apos;s next invoice
                    </span>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {(data?.groups || []).map((group, index) => (
          <Card key={`${group.name}-${index}`} className="bg-slate-900/60 border-slate-800"
            data-testid={`duplicate-group-${index}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex flex-wrap items-center gap-2">
                {group.name}
                <Badge className="bg-amber-500/15 text-amber-300 text-[10px]">
                  {group.count} records
                </Badge>
                {group.needs_a_look && (
                  <Badge className="bg-red-500/15 text-red-300 text-[10px]"
                    data-testid={`needs-a-look-${index}`}>
                    check birthdays
                  </Badge>
                )}
                {group.birthdate && (
                  <span className="text-xs font-normal text-slate-500">born {group.birthdate}</span>
                )}
              </CardTitle>
              <p className="text-xs text-slate-500">
                {group.parent_name ? `Parent: ${group.parent_name}` : "Parent unknown"}
                {group.parent_email ? ` · ${group.parent_email}` : ""}
              </p>
              {group.needs_a_look && (
                <p className="text-[11px] text-red-300 flex items-start gap-1 mt-1"
                  data-testid={`birthday-mismatch-${index}`}>
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  Same name but the birthdays do not match, so these were never grouped before.
                  One is probably a typo — fix the wrong date below, then merge.
                </p>
              )}
              {!group.same_parent && !group.needs_a_look && (
                <p className="text-[11px] text-amber-300 flex items-start gap-1 mt-1"
                  data-testid={`cross-parent-warning-${index}`}>
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  These sit under different parent accounts. Same name and birthday, so it is
                  probably one child whose parent registered twice — but check before merging.
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {group.records.map((rec, recIndex) => (
                <div key={`${rec.player_id}-${recIndex}`}
                  className={`rounded-lg border px-3 py-2 transition-colors ${
                    keepers[index] === rec.player_id
                      ? "border-emerald-500/50 bg-emerald-500/5"
                      : "border-slate-800 bg-slate-950/60"}`}
                  data-testid={`duplicate-record-${rec.player_id}`}>
                  <div className="flex items-start gap-3">
                    <input type="radio" name={`keep-${index}`} className="mt-1"
                      checked={keepers[index] === rec.player_id}
                      onChange={() => setKeepers({ ...keepers, [index]: rec.player_id })}
                      data-testid={`keep-radio-${rec.player_id}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white">
                        {rec.membership_number || "no membership number"}
                        {keepers[index] === rec.player_id && (
                          <Badge className="ml-2 bg-emerald-500/20 text-emerald-300 text-[10px]">
                            keep this one
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-slate-400">
                        {rec.name}
                        {" · born "}
                        <span className={group.records.some((o) => o.birthdate !== rec.birthdate)
                          ? "text-amber-300 font-semibold" : ""}>
                          {rec.birthdate || "not set"}
                        </span>
                      </p>
                      <p className="text-xs text-slate-400">
                        Created {String(rec.created_at || "").slice(0, 10) || "unknown"}
                        {rec.enrolled_sessions ? ` · ${rec.enrolled_sessions} session(s)` : ""}
                        {rec.team_id ? ` · ${rec.team_id}` : ""}
                      </p>
                      <p className="text-[11px] text-slate-500">{describe(rec.attached)}</p>
                    </div>
                    <Button size="sm" variant="ghost"
                      className="h-8 text-[11px] text-slate-400 hover:text-white"
                      onClick={() => setEditing(editing === rec.player_id ? "" : rec.player_id)}
                      data-testid={`edit-record-${rec.player_id}`}>
                      <Pencil className="w-3 h-3 mr-1" />
                      {editing === rec.player_id ? "Close" : "Fix details"}
                    </Button>
                  </div>

                  {editing === rec.player_id && (
                    <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_160px_auto] border-t border-slate-800 pt-3">
                      <Input className="h-9 text-sm" placeholder="Child's name"
                        defaultValue={rec.name}
                        onChange={(e) => setEdits({
                          ...edits,
                          [rec.player_id]: { ...(edits[rec.player_id] || {}), name: e.target.value },
                        })}
                        data-testid={`edit-name-${rec.player_id}`} />
                      <Input className="h-9 text-sm" type="date"
                        defaultValue={rec.birthdate || ""}
                        onChange={(e) => setEdits({
                          ...edits,
                          [rec.player_id]: {
                            ...(edits[rec.player_id] || {}), birthdate: e.target.value,
                          },
                        })}
                        data-testid={`edit-dob-${rec.player_id}`} />
                      <Button size="sm" className="h-9 text-[11px] bg-emerald-600 hover:bg-emerald-500"
                        disabled={savingEdit === rec.player_id}
                        onClick={() => saveRecord(rec)}
                        data-testid={`save-record-${rec.player_id}`}>
                        {savingEdit === rec.player_id
                          ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                        Save
                      </Button>
                      <p className="text-[11px] text-slate-500 sm:col-span-3">
                        Correcting the birthdate here is enough to unblock the merge — you stay on
                        this page.
                      </p>
                    </div>
                  )}
                </div>
              ))}

              <Button size="sm" onClick={() => merge(group, index)}
                disabled={merging === `${index}`}
                className="mt-2 h-9 bg-amber-600 hover:bg-amber-500 text-[11px]"
                data-testid={`merge-group-${index}`}>
                {merging === `${index}` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  : <Merge className="w-3 h-3 mr-1" />}
                Merge the other {group.count - 1} into this record
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
